import React, { useEffect, useMemo, useState } from 'react';
import {
    ArrowLeft,
    Play,
    Forward,
    Heart,
    Share2,
    Crown,
    ListFilter,
    BookOpen,
    ChevronDown,
    Clock,
    Sparkles,
    CheckCircle2,
    Ban,
    BookmarkPlus,
    BookMarked,
    Trash2,
} from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { Select, type SelectOption } from '@/components/ui/Select';
import { api } from '@/utils/api';
import { generateChapterLink } from '@/utils/utils';
import { ObraCoverImage } from '@/components/obra/ObraCoverImage';
import { getObraImageUrl } from '@/utils/image-utils';
import DiscordIcon from '@/components/icons/DiscordIcon';
import WhatsAppIcon from '@/components/icons/WhatsAppIcon';
function normalizeReadingStatusLabel(s: string) {
    return s
        .normalize('NFD')
        .replace(/\p{M}/gu, '')
        .toLowerCase()
        .trim();
}

/** Ícones por rótulo (API) ou por id padrão — alinhado ao painel «Status de leitura». */
function readingStatusOptionIcon(opt: { id?: number; nome?: string }): React.ReactNode {
    const raw = String(opt.nome ?? '').trim();
    const n = normalizeReadingStatusLabel(raw);
    const id = Number(opt.id);

    if (n.includes('planejo') || id === 5) {
        return <BookmarkPlus className="size-[18px] shrink-0 text-violet-400" aria-hidden />;
    }
    if (n.includes('interessado') || id === 1) {
        return <Sparkles className="size-[18px] shrink-0 text-violet-400" aria-hidden />;
    }
    if (n.includes('dropad') || n.includes('dropped') || id === 4) {
        return <Ban className="size-[18px] shrink-0 text-red-500" aria-hidden />;
    }
    if (n.includes('lendo') || id === 2) {
        return <BookOpen className="size-[18px] shrink-0 text-sky-400" aria-hidden />;
    }
    if (n.includes('lido') || id === 3) {
        return <CheckCircle2 className="size-[18px] shrink-0 text-emerald-500" aria-hidden />;
    }
    return <BookOpen className="size-[18px] shrink-0 text-white/45" aria-hidden />;
}

interface ObraHeroProps {
    obra: any;
    isAuthenticated: boolean;
    discordSubscribed: boolean;
    whatsappSubscribed: boolean;
    notificationLoading: boolean;
    /** Listas em que esta obra já está (para resumo na página). */
    listasComObra?: { id: number; nome: string }[];
    onToggleDiscordNotify: () => void;
    onToggleWhatsAppNotify: () => void;
    onShowListModal: () => void;
}

export const ObraHero: React.FC<ObraHeroProps> = ({
    obra,
    isAuthenticated,
    discordSubscribed,
    whatsappSubscribed,
    notificationLoading,
    listasComObra = [],
    onToggleDiscordNotify,
    onToggleWhatsAppNotify,
    onShowListModal,
}) => {
    const navigate = useNavigate();
    const obraId = String(obra?.obr_id ?? obra?.id ?? '');
    const cover = getObraImageUrl(obra?.obr_id ?? obra?.id, obra?.obr_imagem ?? obra?.imagem, 600) || '/placeholder-book.jpg';
    const banner = getObraImageUrl(obra?.obr_id ?? obra?.id, obra?.obr_banner ?? obra?.banner ?? obra?.obr_imagem ?? obra?.imagem, 1280) || cover;
    const title = obra?.obr_nome || obra?.nome || '';
    const rating = Number(obra?.rating ?? obra?.nota ?? 0) || 0;
    const format = obra?.formato?.formt_nome || obra?.formato?.nome || obra?.formato || 'Desconhecido';
    const status = obra?.obr_status || obra?.status || 'Desconhecido';
    const tags = Array.isArray(obra?.tags) ? obra.tags : [];
    const formatoId = obra?.formato?.formt_id ?? obra?.formato?.id ?? obra?.formt_id;
    const synopsis = obra?.obr_descricao || obra?.descricao || 'Sem descrição disponível.';
    const totalChapters = Array.isArray(obra?.capitulos) ? obra.capitulos.length : Number(obra?.total_capitulos_ativos ?? 0);
    const firstChapterId = obra?.primeiro_cap_id ?? (Array.isArray(obra?.capitulos) ? [...obra.capitulos].sort((a: any, b: any) => parseFloat(a.cap_num ?? 0) - parseFloat(b.cap_num ?? 0))[0]?.cap_id : null);
    const lastChapterId = obra?.ultimo_cap_id ?? (Array.isArray(obra?.capitulos) ? [...obra.capitulos].sort((a: any, b: any) => parseFloat(b.cap_num ?? 0) - parseFloat(a.cap_num ?? 0))[0]?.cap_id : null);
    const [notifyActionValue, setNotifyActionValue] = useState('');
    const [isFavoriteLocal, setIsFavoriteLocal] = useState(!!obra?.favoritada);
    const [isTogglingFavorite, setIsTogglingFavorite] = useState(false);
    const [synopsisExpanded, setSynopsisExpanded] = useState(false);
    const [readingStatusLocal, setReadingStatusLocal] = useState<string | number | null>(obra?.status_leitura ?? null);
    const [readingStatusOptions, setReadingStatusOptions] = useState<any[]>([]);

    useEffect(() => {
        setIsFavoriteLocal(!!obra?.favoritada);
    }, [obra?.favoritada, obraId]);

    useEffect(() => {
        setReadingStatusLocal(obra?.status_leitura ?? null);
    }, [obra?.status_leitura, obraId]);

    useEffect(() => {
        if (!isAuthenticated) {
            setReadingStatusOptions([]);
            return;
        }

        const loadReadingStatuses = async () => {
            try {
                const response = await api.get('readings/statuses');
                const data = response?.data ?? response;
                setReadingStatusOptions(Array.isArray(data) ? data : []);
            } catch {
                setReadingStatusOptions([]);
            }
        };

        loadReadingStatuses();
    }, [isAuthenticated]);

    const readingStatusSelectOptions: SelectOption[] = useMemo(() => {
        const rows =
            readingStatusOptions?.map((opt: any) => {
                const id = Number(opt.id ?? opt.stt_id);
                const nome = String(opt.nome ?? opt.stt_nome ?? '');
                return {
                    value: String(opt.id ?? opt.stt_id),
                    label: nome,
                    icon: readingStatusOptionIcon({ id, nome }),
                };
            }) ?? [];
        const tail: SelectOption[] = readingStatusLocal
            ? [
                  {
                      value: 'remove',
                      label: 'Remover do histórico',
                      icon: <Trash2 className="size-[18px] shrink-0 text-rose-400" aria-hidden />,
                  },
              ]
            : [];
        return [
            {
                value: '',
                label: 'Status de leitura',
                disabled: true,
                icon: <BookMarked className="size-[18px] shrink-0 text-orange-500/90" aria-hidden />,
            },
            ...rows,
            ...tail,
        ];
    }, [readingStatusOptions, readingStatusLocal]);
    const notifyOptions = [
        { value: '', label: 'Notificar', disabled: true },
        {
            value: 'discord',
            label: discordSubscribed ? 'Discord (ativo)' : 'Discord',
            icon: <DiscordIcon size={18} className="text-[#5865F2]" />,
        },
        {
            value: 'whatsapp',
            label: whatsappSubscribed ? 'WhatsApp (ativo)' : 'WhatsApp',
            icon: <WhatsAppIcon size={18} className="text-[#25D366]" />,
        },
    ];

    const handleNotifyChange = async (e: React.ChangeEvent<HTMLSelectElement>) => {
        const selectedChannel = e.target.value;
        if (!selectedChannel || notificationLoading) return;

        if (selectedChannel === 'discord') {
            await onToggleDiscordNotify();
        } else if (selectedChannel === 'whatsapp') {
            await onToggleWhatsAppNotify();
        }

        setNotifyActionValue('');
    };

    const handleToggleFavorite = async () => {
        if (isTogglingFavorite) return;
        if (!isAuthenticated) {
            navigate('/entrar');
            return;
        }

        const nextValue = !isFavoriteLocal;
        setIsFavoriteLocal(nextValue);
        setIsTogglingFavorite(true);

        try {
            await api.post(`favorites/toggle/${obraId}`, {});
        } catch (error) {
            console.error('Erro ao favoritar/desfavoritar:', error);
            setIsFavoriteLocal(!nextValue);
        } finally {
            setIsTogglingFavorite(false);
        }
    };

    const handleGoBack = () => navigate(-1);

    const handleReadFirst = () => {
        if (!firstChapterId) return;
        navigate(generateChapterLink(firstChapterId));
    };

    const handleReadLast = () => {
        if (!lastChapterId) return;
        navigate(generateChapterLink(lastChapterId));
    };

    const handleToggleSynopsis = () => setSynopsisExpanded((prev) => !prev);

    const handleReadingStatusChange = async (e: React.ChangeEvent<HTMLSelectElement>) => {
        const val = e.target.value;
        if (!val || !obraId) return;
        if (!isAuthenticated) {
            navigate('/entrar');
            return;
        }

        if (val === 'remove') {
            if (!window.confirm('Tem certeza que deseja remover esta obra do seu histórico?')) return;
            try {
                await api.delete(`readings/${obraId}`);
                setReadingStatusLocal(null);
            } catch (err) {
                console.error('Erro ao remover do histórico:', err);
            }
            return;
        }

        const statusId = parseInt(val);
        if (Number.isNaN(statusId)) return;
        try {
            await api.post('readings/status', { obr_id: parseInt(obraId), stt_id: statusId });
            setReadingStatusLocal(statusId);
        } catch (err) {
            console.error('Erro ao atualizar status de leitura:', err);
        }
    };

    return (
        <>
            {/* Banner Background com Blur e Gradiente */}
            <div className="absolute top-0 left-0 w-full h-[500px] overflow-hidden z-0">
                <ObraCoverImage
                    src={banner}
                    alt=""
                    className="absolute inset-0 h-full w-full object-cover object-center opacity-30 scale-110"
                    iconClassName="h-48 w-48 text-white/15"
                />
                <div className="absolute inset-0 bg-gradient-to-b from-neutral-950/20 via-neutral-950/80 to-neutral-950" />
            </div>

            <main className="relative z-10 max-w-7xl mx-auto px-4 md:px-8 pt-24 md:pt-32">
                {/* Botão Voltar */}
                <button
                    onClick={handleGoBack}
                    className="flex items-center gap-2 text-white/60 hover:text-white mb-6 md:mb-8 transition-colors group"
                >
                    <ArrowLeft size={20} className="group-hover:-translate-x-1 transition-transform" />
                    <span className="font-medium">Voltar</span>
                </button>

                {/* Mobile Layout */}
                <div className="md:hidden space-y-6">
                    {/* Capa Mobile - Centralizada */}
                    <div className="flex justify-center">
                        <div className="relative w-[180px] aspect-[2/3] rounded-xl overflow-hidden border border-white/10">
                            <ObraCoverImage src={cover} alt={title} className="h-full w-full object-cover" />
                            {rating > 0 && (
                                <div className="absolute top-2 right-2 bg-black/60 text-yellow-400 font-bold px-2 py-0.5 rounded text-xs">
                                    ★ {rating.toFixed(1)}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Título e Tags Mobile */}
                    <div className="text-center space-y-2">
                        <div className="flex flex-wrap items-center justify-center gap-1.5">
                            {(obra.vip || obra.obr_vip) && (
                                <span className="px-2 py-0.5 rounded text-[10px] font-bold uppercase text-yellow-500 border border-yellow-500/30">
                                    VIP
                                </span>
                            )}
                            <span className="px-2 py-0.5 rounded text-[10px] font-bold uppercase text-white/50 border border-white/10">
                                {format}
                            </span>
                            <span className="px-2 py-0.5 rounded text-[10px] font-bold uppercase text-green-500 border border-green-500/30">
                                {status}
                            </span>
                        </div>

                        <h1 className="text-2xl font-black text-white leading-tight px-2">
                            {title}
                        </h1>

                        <div className="flex items-center justify-center gap-1 text-xs text-white/40">
                            <ListFilter size={12} />
                            <span>{totalChapters} Capítulos</span>
                        </div>
                    </div>

                    {/* Botões de Ação Mobile */}
                    <div className="space-y-2">
                        <button
                            onClick={handleReadFirst}
                            className="w-full bg-orange-600 hover:bg-orange-500 text-white font-bold py-3.5 px-6 rounded-xl flex items-center justify-center gap-2 transition-colors"
                        >
                            <Play size={18} fill="currentColor" />
                            <span>Ler Agora</span>
                        </button>

                        <div className="grid grid-cols-2 gap-2">
                            {obra?.obra_novel && (
                                <Link
                                    to={`/obra/${obra.obra_novel.id}`}
                                    className="bg-white/5 hover:bg-white/10 text-sky-400 border border-white/10 font-medium py-2.5 px-4 rounded-xl flex items-center justify-center gap-2 transition-colors text-sm"
                                >
                                    <BookOpen size={16} />
                                    <span>Novel</span>
                                </Link>
                            )}
                            {obra?.obra_original && (
                                <Link
                                    to={`/obra/${obra.obra_original.id}`}
                                    className="bg-white/5 hover:bg-white/10 text-purple-400 border border-white/10 font-medium py-2.5 px-4 rounded-xl flex items-center justify-center gap-2 transition-colors text-sm"
                                >
                                    <BookOpen size={16} />
                                    <span>Ler {obra.obra_original.formato?.nome || 'Manga'}</span>
                                </Link>
                            )}
                            <button
                                onClick={handleReadLast}
                                className="bg-white/5 hover:bg-white/10 text-white/75 py-2.5 px-4 rounded-xl flex items-center justify-center gap-2 transition-colors border border-white/10 text-sm font-medium"
                            >
                                <Forward size={16} />
                                <span>Último</span>
                            </button>
                            <button
                                onClick={handleToggleFavorite}
                                disabled={isTogglingFavorite}
                                className={`py-2.5 px-4 rounded-xl flex items-center justify-center gap-2 transition-colors border text-sm ${isFavoriteLocal
                                    ? 'text-red-400 border-red-500/30 '
                                    : 'text-white/50 border-white/10  hover:bg-white/10'
                                    }`}
                            >
                                <Heart size={16} fill={isFavoriteLocal ? "currentColor" : "none"} />
                                <span>{isFavoriteLocal ? 'Favorito' : 'Favoritar'}</span>
                            </button>
                        </div>

                        <button
                            type="button"
                            onClick={onShowListModal}
                            className="w-full bg-white/5 hover:bg-white/10 border border-white/10 text-white/80 py-2.5 px-4 rounded-xl flex items-center justify-center gap-2 transition-colors text-sm font-medium"
                        >
                            <LinkPlus size={18} />
                            <span>Adicionar à lista</span>
                        </button>


                        {/* Status Select Mobile */}
                        {isAuthenticated && (
                            <Select
                                value={String(readingStatusLocal || '')}
                                onChange={handleReadingStatusChange}
                                options={readingStatusSelectOptions}
                                className="w-full"
                                selectClassName="rounded-xl text-center"
                                mobileBottomDrawer
                                drawerTitle="Status de leitura"
                            />
                        )}

                        {/* Notificar (mobile) */}
                        {isAuthenticated && (
                            <Select
                                value={notifyActionValue}
                                onChange={handleNotifyChange}
                                options={notifyOptions}
                                className="w-full"
                                disabled={notificationLoading}
                                selectClassName={`rounded-xl text-center ${discordSubscribed || whatsappSubscribed ? 'text-indigo-400 border-indigo-500/30' : ''}`}
                                mobileBottomDrawer
                                drawerTitle="Notificar"
                            />
                        )}
                    </div>

                    {/* Gêneros Mobile */}
                    {tags.length > 0 && (
                        <div className="flex flex-wrap gap-2 justify-center">
                            {tags.map((tag: any) => (
                                (() => {
                                    const tagId = tag?.tag_id ?? tag?.id ?? tag?.tag?.tag_id;
                                    const tagName = tag?.tag_nome ?? tag?.nome ?? tag?.tag?.tag_nome ?? tag?.tag?.nome;
                                    if (!tagId || !tagName) return null;
                                    return (
                                <Link
                                    key={tagId}
                                    to={`/pesquisar?tags=${tagId}${formatoId ? `&formato=${formatoId}` : ''}`}
                                    className="px-3 py-1.5 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 text-white/80 hover:text-white text-xs font-medium transition-all"
                                >
                                    {tagName}
                                </Link>
                                    );
                                })()
                            ))}
                        </div>
                    )}

                    {/* Sinopse Mobile */}
                    <div>
                        <div className={`text-white/60 text-sm leading-relaxed ${!synopsisExpanded ? 'line-clamp-4' : ''}`}>
                            {synopsis}
                        </div>
                        {synopsis.length > 250 && (
                            <button
                                onClick={handleToggleSynopsis}
                                className="mt-2 text-orange-500 text-xs font-bold hover:text-orange-400 flex items-center gap-1"
                            >
                                {synopsisExpanded ? (
                                    <>Ver Menos <ChevronDown className="rotate-180" size={12} /></>
                                ) : (
                                    <>Ver Mais <ChevronDown size={12} /></>
                                )}
                            </button>
                        )}
                    </div>
                </div>

                {/* Desktop Layout Header */}
                <div className="hidden md:flex flex-col md:flex-row gap-10 lg:gap-16 mb-12">
                    {/* Coluna da Capa */}
                    <div className="flex-shrink-0 w-[260px]">
                        <div className="relative aspect-[2/3] rounded-xl overflow-hidden border border-white/10">
                            <ObraCoverImage src={cover} alt={title} className="h-full w-full object-cover" />
                            {rating > 0 && (
                                <div className="absolute top-2 right-2 bg-black/60 text-yellow-400 font-bold px-2 py-0.5 rounded text-xs">
                                    ★ {rating.toFixed(1)}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Coluna de Informações Desktop */}
                    <div className="flex-1 flex flex-col">
                        <div className="flex flex-wrap items-center gap-1.5 md:gap-3 mb-3">
                            {(obra.vip || obra.obr_vip) && (
                                <span className="px-2 py-0.5 rounded text-[10px] font-bold uppercase text-yellow-500 border border-yellow-500/30">
                                    VIP
                                </span>
                            )}
                            <span className="px-2 py-0.5 rounded text-[10px] font-bold uppercase text-white/40 border border-white/10">
                                {format}
                            </span>
                            <span className="px-2 py-0.5 rounded text-[10px] font-bold uppercase text-green-500 border border-green-500/30">
                                {status}
                            </span>
                        </div>

                        <h1 className="text-3xl font-bold text-white mb-2 leading-tight">
                            {title}
                        </h1>

                        <div className="flex items-center gap-1.5 text-xs text-white/40 mb-5">
                            <ListFilter size={13} />
                            <span>{totalChapters} Capítulos</span>
                        </div>

                        {/* Gêneros Desktop */}
                        {tags.length > 0 && (
                            <div className="flex flex-wrap gap-1.5 md:gap-3 mb-5">
                                {tags.map((tag: any) => (
                                    (() => {
                                        const tagId = tag?.tag_id ?? tag?.id ?? tag?.tag?.tag_id;
                                        const tagName = tag?.tag_nome ?? tag?.nome ?? tag?.tag?.tag_nome ?? tag?.tag?.nome;
                                        if (!tagId || !tagName) return null;
                                        return (
                                    <Link
                                        key={tagId}
                                        to={`/pesquisar?tags=${tagId}${formatoId ? `&formato=${formatoId}` : ''}`}
                                        className="px-2.5 py-1 rounded-lg bg-white/5 hover:bg-white/10 border border-white/5 text-xs text-white/50 hover:text-white/80 transition-colors"
                                    >
                                        {tagName}
                                    </Link>
                                        );
                                    })()
                                ))}
                            </div>
                        )}

                        {/* Sinopse Desktop */}
                        <div className="relative mb-6">
                            <div className={`text-white/60 text-sm leading-relaxed ${!synopsisExpanded ? 'line-clamp-4' : ''}`}>
                                {synopsis}
                            </div>
                            {synopsis.length > 300 && (
                                <button
                                onClick={handleToggleSynopsis}
                                    className="mt-1.5 text-orange-500 text-xs font-bold hover:text-orange-400 flex items-center gap-1"
                                >
                                    {synopsisExpanded ? (
                                        <>Ver Menos <ChevronDown className="rotate-180" size={12} /></>
                                    ) : (
                                        <>Ver Mais <ChevronDown size={12} /></>
                                    )}
                                </button>
                            )}
                        </div>

                        {/* Ações Desktop */}
                        <div className="flex flex-wrap items-center gap-3 md:gap-4 lg:gap-5 mb-8">
                            <button
                                onClick={handleReadFirst}
                                className="bg-orange-600 hover:bg-orange-500 text-white text-sm font-bold py-2.5 px-5 rounded-lg flex items-center gap-2 transition-colors"
                            >
                                <Play size={16} fill="currentColor" />
                                <span>Ler Primeiro</span>
                            </button>
                            {obra?.obra_novel && (
                                <Link
                                    to={`/obra/${obra.obra_novel.id}`}
                                    className="bg-white/5 hover:bg-white/10 text-sky-400 text-sm font-medium py-2.5 px-4 rounded-lg flex items-center gap-2 transition-colors border border-white/10"
                                >
                                    <BookOpen size={16} />
                                    <span>Ler Novel</span>
                                </Link>
                            )}
                            {obra?.obra_original && (
                                <Link
                                    to={`/obra/${obra.obra_original.id}`}
                                    className="bg-white/5 hover:bg-white/10 text-purple-400 text-sm font-medium py-2.5 px-4 rounded-lg flex items-center gap-2 transition-colors border border-white/10"
                                >
                                    <BookOpen size={16} />
                                    <span>Ler {obra.obra_original.formato?.nome || 'Manga'}</span>
                                </Link>
                            )}
                            <button
                                onClick={handleReadLast}
                                className="bg-white/5 hover:bg-white/10 border border-white/10 text-white/75 text-sm font-bold py-2.5 px-5 rounded-lg flex items-center gap-2 transition-colors"
                            >
                                <Forward size={16} />
                                <span>Ler Último</span>
                            </button>
                            <button
                                onClick={handleToggleFavorite}
                                disabled={isTogglingFavorite}
                                className={`p-2.5 rounded-lg transition-colors border ${isFavoriteLocal ? 'border-red-500/30 text-red-400 ' : 'border-white/10 text-white/40hover:bg-white/10'}`}
                                title={isFavoriteLocal ? "Remover dos favoritos" : "Adicionar aos favoritos"}
                            >
                                <Heart size={16} fill={isFavoriteLocal ? "currentColor" : "none"} />
                            </button>
                            <button
                                onClick={onShowListModal}
                                className="p-2.5 rounded-lg transition-colors border border-white/10 text-white/40 hover:bg-white/10 hover:text-white/70"
                                title="Adicionar à Lista"
                            >
                                <LinkPlus size={16} />
                            </button>

                            {/* Status Select Desktop */}
                            {isAuthenticated && (
                                <Select
                                    value={String(readingStatusLocal || '')}
                                    onChange={handleReadingStatusChange}
                                    options={readingStatusSelectOptions}
                                    selectClassName="text-white/60"
                                />
                            )}

                            {/* Notificar (desktop) */}
                            {isAuthenticated && (
                                <Select
                                    value={notifyActionValue}
                                    onChange={handleNotifyChange}
                                    options={notifyOptions}
                                    disabled={notificationLoading}
                                    selectClassName={`${discordSubscribed || whatsappSubscribed ? 'text-indigo-400 border-indigo-500/30' : 'text-white/40'}`}
                                    mobileBottomDrawer
                                    drawerTitle="Notificar"
                                />
                            )}
                        </div>
                    </div>
                </div>
            </main>
        </>
    );
};

const LinkPlus = ({ size }: { size: number }) => (
    <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 12H3" /><path d="M16 6H3" /><path d="M16 18H3" /><path d="M18 9v6" /><path d="M21 12h-6" /></svg>
);
